// BUILD-046 J1a (Customer association): how a task's customer is shown, and when the Compact "Customer" column exists.
//
// WHERE IT SHOWS. Comfortable: the customer name is the FIRST part of the one quiet second line under the title (beside the requester badge), so it reads
// "Alvarez - Pool build - By Julie - Oct 18". Compact: the customer is NEVER drawn inline after the title; it has its OWN aligned "Customer" column right after
// Task (before the optional Requested column). The server gives the CURRENT name when the customer directory answered (Owner decision 3), the last known name when
// it could not be asked, and a flag when the customer is inactive or no longer known; this module words those states, nothing more.
//
// A row the viewer sees only in its minimal (redacted) form carries no customer and shows none. The label is shown to anyone who may see the task (Owner
// decision 2); it grants no access to the customer itself.
//
// WHEN THE COMPACT COLUMN EXISTS. In Compact, and only when customers are "in play" for the page: the Caller may search customers (`GET /work/me` `canSearchCustomers`), or at
// least one task the page holds carries a customer (a viewer who cannot search still sees a label that is there). The page publishes that one boolean to a tiny
// store, and the table header and every row read the SAME store, so a heading and its cells can never disagree (the pattern of the "Requested" column). Against a
// server that predates customers nothing is in play, so the page is exactly what it was.
//
// Pure module: no React, no I/O. Erasable TypeScript; type-only import.

import type { Density } from './densityStore';
import { stripTestMarker } from './displayNames';

/** The customer of a task or request, validated: the stable id, the name to show, and what is known about its state. */
export interface CustomerShown {
  customerId: string;
  label: string;
  state?: 'active' | 'inactive';
  lastKnown?: true;
  missing?: true;
}

/** A well-formed customer view from the server, or null. Anything else (absent, wrong type, empty name) is no customer. */
export function asCustomerShown(value: unknown): CustomerShown | null {
  if (value === null || typeof value !== 'object') return null;
  const v = value as { customerId?: unknown; label?: unknown; state?: unknown; lastKnown?: unknown; missing?: unknown };
  if (typeof v.customerId !== 'string' || v.customerId === '' || typeof v.label !== 'string') return null;
  const label = v.label.replace(/\s+/g, ' ').trim();
  if (label === '') return null;
  const out: CustomerShown = { customerId: v.customerId, label };
  if (v.state === 'active' || v.state === 'inactive') out.state = v.state;
  if (v.lastKnown === true) out.lastKnown = true;
  if (v.missing === true) out.missing = true;
  return out;
}

/** The customer a task row shows: none for a task shown in its minimal (restricted) form or one that carries none. */
export function customerOf(task: unknown): CustomerShown | null {
  if (task === null || typeof task !== 'object') return null;
  const t = task as { customer?: unknown; redacted?: unknown };
  if (t.redacted === true) return null;
  return asCustomerShown(t.customer);
}

/**
 * The customer of one entry of `GET /work/issued`: an Everyone request carries it on the entry, any other request on its task. A task in its minimal form carries
 * none.
 */
export function customerOfIssuedEntry(raw: unknown): CustomerShown | null {
  if (raw === null || typeof raw !== 'object') return null;
  const entry = raw as { customer?: unknown; item?: unknown };
  return asCustomerShown(entry.customer) ?? customerOf(entry.item);
}

export interface CustomerLine {
  /** The name to show. */
  label: string;
  /** A short quiet word after the name when the name is not the live one: `last known name`, `not found` or `inactive`; null when it is. */
  note: string | null;
  /** The hover text: the name, and why the note applies. */
  title: string;
}

/**
 * How one customer reads: its name, and an honest quiet note when the name may be out of date or the customer is retired. The name is SHOWN without the "(FICTIONAL)" test
 * marker (displayNames.stripTestMarker); the stored name on the customer, the filter value (the customer id) and what search and sort read are not changed.
 */
export function customerLineOf(customer: CustomerShown): CustomerLine {
  const label = stripTestMarker(customer.label);
  if (customer.missing === true) {
    return { label, note: 'not found', title: `${label}. This customer is no longer in the customer list; this is its last known name.` };
  }
  if (customer.lastKnown === true) {
    return { label, note: 'last known name', title: `${label}. The customer list could not be checked, so this is the last known name.` };
  }
  if (customer.state === 'inactive') {
    return { label, note: 'inactive', title: `${label}. This customer is inactive.` };
  }
  return { label, note: null, title: label };
}

/** Whether any of the lists holds a task that carries a customer. */
export function anyCustomer(lists: readonly (readonly unknown[])[]): boolean {
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const row of list) if (customerOf(row) !== null) return true;
  }
  return false;
}

/** Customers are "in play" for the page: the Caller may search them, or a task the page holds carries one. */
export function customersInPlay(canSearchCustomers: boolean, lists: readonly (readonly unknown[])[]): boolean {
  return canSearchCustomers === true || anyCustomer(lists);
}

/** The "Customer" column exists only in Compact, and only while customers are in play. Comfortable shows the customer under the title instead. */
export function customerColumnShown(density: unknown, inPlay: boolean): boolean {
  return density === 'compact' && inPlay === true;
}

/** The customer in the second line is drawn in Comfortable only; Compact has the column. */
export function customerInSecondLine(density: Density): boolean {
  return density === 'comfortable';
}

// ───────────────────────── ONE association column (Customer beside Job) ─────────────────────────
//
// A task can carry a Customer AND a Job (two separate associations). The Compact table still has AT MOST ONE column for them, never two:
//   - customers in play: the column is headed "Customer"; its cell shows the customer, and when the row also carries a job, a smaller "Job: <name>" line under it;
//   - customers NOT in play, jobs in play: exactly the column that was there before customers existed, headed "Customer / Job", showing the job;
//   - neither in play, or not Compact: no column (Comfortable shows the customer, then the job, in the one quiet second line).

/** Which association column a table has: none, the Customer column (which also shows a row's job), or the original Customer / Job column. */
export type AssociationColumn = 'none' | 'customer' | 'job';

/** The column of a Compact table, from the density and the two "in play" flags. */
export function associationColumnOf(density: unknown, customersInPlayFlag: boolean, jobsInPlayFlag: boolean): AssociationColumn {
  if (density !== 'compact') return 'none';
  if (customersInPlayFlag === true) return 'customer';
  return jobsInPlayFlag === true ? 'job' : 'none';
}

/**
 * The words over the association column: "Customer" when customers are in play, else the original "Customer / Job". Null when the table has no such column.
 * Team and Company write the JOB first in that cell (`jobFirst`), so while customers are in play their heading is "Job / Customer": the heading names what the cell shows, in the order it shows it.
 * (Without customers in play the column is the original job column and keeps the original words in every table.)
 */
export function associationHeading(column: AssociationColumn, jobFirst: boolean = false): string | null {
  if (column === 'customer') return jobFirst ? 'Job / Customer' : 'Customer';
  return column === 'job' ? 'Customer / Job' : null;
}

/** The words that label each line of the association cell when the job comes first. A label is written only when the cell shows BOTH, or when the heading does not already say which it is. */
export const JOB_LABEL = 'Job:';
export const CUSTOMER_LABEL = 'Customer:';

/** The smaller line a row's job is written on under its customer: "Job: Pool build". The prefix keeps a customer and a job from reading as two customers. */
export const JOB_LINE_PREFIX = 'Job: ';

// ───────────────────────── the page's "customers in play" flag ─────────────────────────

export interface CustomersInPlayStore {
  get: () => boolean;
  set: (next: boolean) => void;
  subscribe: (listener: () => void) => () => void;
}

/** One boolean for the whole My Work page. Not stored anywhere: the page recomputes it from `GET /work/me` and the lists it holds. */
export function createCustomersInPlayStore(): CustomersInPlayStore {
  let value = false;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      if (typeof next !== 'boolean' || next === value) return;
      value = next;
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The page's flag; the table header and the rows read it (useCustomerColumn.ts). */
export const customersInPlayStore: CustomersInPlayStore = createCustomersInPlayStore();
